import { createHttpClient } from '@adapters/HttpClient';
import { faBarcode, faImage } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ProductData = forwardRef(
  (
    {
      nombre,
      setNombre,
      descripcion,
      setDescripcion,
      codigo,
      setCodigo,
      coste,
      setCoste,
      categoria,
      setCategoria,
      imagenPreview,
      setImagenPreview,
      imageFile,
      setImageFile,
      length,
      setLength,
      heigth,
      setHeigth,
      width,
      setWidth,
      weight,
      setWeight,
      weightUnit,
      setWeightUnit,
      dimensionUnit,
      setDimensionUnit,
      categorias,
      loadingCategorias,
      readOnly = false,
    }: any,
    ref,
  ) => {
    const { t } = useTranslation('createEditProductsModule');
    const { toast } = useToast();
    const [loadingCodigo, setLoadingCodigo] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      getFormData: () => ({
        name: nombre,
        description: descripcion,
        code: codigo,
        cost: coste,
        categoryId: categoria,
        imageFile,
        length,
        heigth,
        width,
        weight,
        weight_unit: weightUnit,
        dimension_unit: dimensionUnit,
      }),
    }));

    const httpClient = createHttpClient(apiClientWH);

    const handleGenerarCodigo = async () => {
      setLoadingCodigo(true);
      try {
        const resp: any = await httpClient.get('/products/generate/code');
        if (resp && resp.code) {
          setCodigo(resp.code);
          toast({
            title: t('form.codigo_generado_titulo'),
            description: t('form.codigo_generado_desc'),
            variant: 'success',
          });
        } else {
          throw new Error('No se recibió código');
        }
      } catch {
        toast({
          title: t('form.codigo_error_titulo'),
          description: t('form.codigo_error_desc'),
          variant: 'destructive',
        });
      }
      setLoadingCodigo(false);
    };

    const handleImageButtonClick = () => {
      fileInputRef.current?.click();
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        const validTypes = ['image/jpeg', 'image/png', 'image/gif'];
        const maxSize = 4 * 1024 * 1024; // 4MB
        if (!validTypes.includes(file.type)) {
          toast({
            title: t('form.imagen_error_titulo'),
            description: t('form.imagen_error_desc'),
            variant: 'destructive',
          });
          return;
        }
        if (file.size > maxSize) {
          toast({
            title: t('form.imagen_error_titulo'),
            description: t('form.imagen_error_peso'),
            variant: 'destructive',
          });
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          setImagenPreview(ev.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    };

    return (
      <>
        <form>
          {/* Resumen */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.resumen_title')}</h2>
              <p className="text-sm">{t('sections.resumen_desc')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              {/* Nombre del producto */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label htmlFor="nombre" className="pb-2">
                    {t('form.nombre_label')}
                  </Label>
                  <Input
                    id="nombre"
                    placeholder={t('form.nombre_placeholder')}
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-name-input"
                  />
                </div>
                {/* Código y Categoría */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="codigo" className="pb-2">
                    {t('form.codigo_label')}
                  </Label>
                  <div className="flex">
                    <Input
                      id="codigo"
                      placeholder={t('form.codigo_placeholder')}
                      className="flex-1 rounded-r-none"
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value)}
                      disabled={loadingCodigo || readOnly}
                      data-testid="product-code-input"
                    />
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="secondary"
                        className={`px-4 py-1 text-sm font-medium bg-lines h-10 ml-[-3px] flex items-center gap-2 light:!bg-text light:text-white${loadingCodigo ? ' opacity-60 pointer-events-none' : ''}`}
                        onClick={handleGenerarCodigo}
                        disabled={loadingCodigo}
                        data-testid="generate-code-button"
                      >
                        <FontAwesomeIcon icon={faBarcode} className="!w-3 !h-3" />
                        {t('form.generar')}
                      </Button>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="categoria" className="pb-2">
                    {t('form.categoria_label')}
                  </Label>
                  <Combobox
                    options={categorias.map((cat: any) => ({
                      value: cat.id,
                      label: cat.name,
                    }))}
                    placeholder={loadingCategorias ? 'Cargando categorías...' : t('form.categoria_placeholder')}
                    value={categoria}
                    setValue={setCategoria}
                    dataTestId="combobox-categoria"
                    data-testid="product-category-combobox"
                    disabled={loadingCategorias || readOnly}
                  />
                </div>
                {/* Descripción */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label htmlFor="codigo" className="pb-2">
                    {t('form.descripcion_label')}
                  </Label>
                  <TextArea
                    id="descripcion"
                    name="descripcion"
                    placeholder={t('form.descripcion_placeholder')}
                    rows={4}
                    value={descripcion}
                    onChange={(e) => setDescripcion(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-description-textarea"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Imagen del producto */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.imagen_title')}</h2>
              <p className="text-sm">{t('sections.imagen_desc')}</p>
            </div>
            <div className="col-span-1 md:col-span-4 flex flex-col md:flex-row md:items-center gap-5 !mt-0">
              <div className="relative flex items-center justify-center w-24 h-24 mx-auto md:mx-0">
                <span className="w-24 h-24 rounded-full bg-hover-press flex items-center justify-center relative overflow-hidden">
                  {imagenPreview ? (
                    <img src={imagenPreview} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <FontAwesomeIcon icon={faImage} className="w-4 h-4 text-complementary-text" />
                  )}
                </span>
                {!readOnly && (
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      handleImageChange(e);
                      const file = e.target.files?.[0];
                      if (file) setImageFile(file);
                    }}
                    data-testid="product-image-file-input"
                  />
                )}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="absolute bottom-0 right-0 w-6 h-6 p-0 rounded shadow-md bg-hover-press"
                    onClick={handleImageButtonClick}
                    data-testid="product-image-upload-button"
                  >
                    <FontAwesomeIcon icon={faImage} className="!w-3 !h-3 text-complementary-text" />
                  </Button>
                )}
              </div>
              <div className="text-center md:text-left">
                <div className="mb-1">{t('sections.imagen_help')}</div>
                <div className="text-sm text-complementary-text">{t('sections.imagen_formatos')}</div>
              </div>
            </div>
          </div>

          {/* Información adicional */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.infoadicional_title')}</h2>
              <p className="text-sm">{t('sections.infoadicional_desc')}</p>
            </div>
            <div className="col-span-1 md:col-span-4  !mt-0">
              {/* Coste y Peso */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start mb-4">
                <div className="flex flex-col gap-1 items-start pt-1">
                  <Label htmlFor="coste" className="pb-2">
                    {t('form.coste_label')}
                  </Label>
                  <Input
                    id="coste"
                    placeholder={t('form.coste_placeholder')}
                    value={coste}
                    onChange={(e) => setCoste(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-cost-input"
                  />
                </div>
                <div className="flex flex-col gap-1 items-start ">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <Label htmlFor="peso" className="flex-1 pb-2">
                      {t('form.peso_label')}
                    </Label>
                    <Select value={weightUnit} onValueChange={setWeightUnit} disabled={readOnly}>
                      <SelectTrigger
                        className="w-12 border-none bg-transparent px-0 py-0 shadow-none h-auto min-w-0"
                        data-testid="weight-unit-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="kg">{t('form.unidad_kg')}</SelectItem>
                        <SelectItem value="g">g</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    id="peso"
                    placeholder={t('form.peso_placeholder')}
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-weight-input"
                  />
                </div>
              </div>
              {/* Dimensiones */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                <div className="flex flex-col gap-1 items-start">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <Label htmlFor="ancho" className="flex-1 pb-2">
                      {t('form.ancho_label')}
                    </Label>
                  </div>
                  <Input
                    id="ancho"
                    placeholder={t('form.ancho_placeholder')}
                    value={length}
                    onChange={(e) => setLength(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-width-input"
                  />
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <Label htmlFor="alto" className="flex-1 pb-2">
                      {t('form.alto_label')}
                    </Label>
                  </div>
                  <Input
                    id="alto"
                    placeholder={t('form.alto_placeholder')}
                    value={heigth}
                    onChange={(e) => setHeigth(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-height-input"
                  />
                </div>
                <div className="flex flex-col gap-1 items-start">
                  <div className="flex items-center justify-between gap-2 w-full">
                    <Label htmlFor="largo" className="flex-1 pb-2">
                      {t('form.largo_label')}
                    </Label>
                    <Select value={dimensionUnit} onValueChange={setDimensionUnit} disabled={readOnly}>
                      <SelectTrigger
                        className="w-12 border-none bg-transparent px-0 py-0 shadow-none h-auto min-w-0"
                        data-testid="dimension-unit-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cm">{t('form.unidad_cm')}</SelectItem>
                        <SelectItem value="mt">{t('form.unidad_mt')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Input
                    id="largo"
                    placeholder={t('form.largo_placeholder')}
                    value={width}
                    onChange={(e) => setWidth(e.target.value)}
                    disabled={readOnly}
                    data-testid="product-length-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </>
    );
  },
);
